import type { Chat as ChatData, ChatMessage as Message } from "api-client";
import { makeAutoObservable } from "mobx";
import { mapBy } from "utils";

export type { ChatData, Message };

interface Member {
  id: string;
  name: string;
}

interface NewChat {
  members: Member[];
}

function compareChatsByRecency(left: ChatData, right: ChatData) {
  const leftAt = left.lastMessage?.createdAt ?? null;
  const rightAt = right.lastMessage?.createdAt ?? null;

  if (leftAt === rightAt) {
    return right.id.localeCompare(left.id);
  }

  if (leftAt === null) {
    return 1;
  }

  if (rightAt === null) {
    return -1;
  }

  return rightAt.localeCompare(leftAt);
}

export class ChatStore {
  chats: ChatData[];
  messagesByChatId: Record<string, Message[]>;
  lastReadAtByChatId: Record<string, string>;
  newChat: NewChat | null;

  constructor() {
    this.chats = [];
    this.messagesByChatId = {};
    this.lastReadAtByChatId = {};
    this.newChat = null;

    makeAutoObservable(this);
  }

  mergeChats(chats: ChatData[]) {
    const byId = new Map<string, ChatData>();

    for (const chat of this.chats) {
      byId.set(chat.id, chat);
    }

    for (const chat of chats) {
      byId.set(chat.id, chat);
    }

    this.chats = Array.from(byId.values()).sort(compareChatsByRecency);
  }

  setMessages(chatId: string, messages: Message[]) {
    this.mergeMessages(chatId, messages);
  }

  get chatsById() {
    return mapBy(this.chats, (x) => {
      return x.id;
    });
  }

  get isCreatingNewChat() {
    return this.newChat !== null;
  }

  get totalUnreadCount() {
    return this.chats.reduce((sum, chat) => {
      return sum + chat.unreadCount;
    }, 0);
  }

  getMessageById(messageId: string): Message | undefined {
    for (const chatMessages of Object.values(this.messagesByChatId)) {
      const message = chatMessages.find(function (m) {
        return m.id === messageId;
      });

      if (message) {
        return message;
      }
    }

    return undefined;
  }

  getNewestMessageId(chatId: string): string | null {
    const messages = this.messagesByChatId[chatId];
    return messages?.at(-1)?.id ?? null;
  }

  getNewestUnreadMessageFromOther(chatId: string): Message | null {
    const chat = this.chatsById[chatId];
    const messages = this.messagesByChatId[chatId];

    if (!chat || !messages || chat.unreadCount === 0) {
      return null;
    }

    for (let i = messages.length - 1; i >= 0; i--) {
      if (!messages[i].wasSentBySameUser) {
        return messages[i];
      }
    }

    return null;
  }

  updateMessage(chatId: string, messageId: string, updates: Partial<Message>) {
    const messages = this.messagesByChatId[chatId];

    if (!messages) {
      return;
    }

    const index = messages.findIndex(function (m) {
      return m.id === messageId;
    });

    if (index === -1) {
      return;
    }

    const original = messages[index];
    messages[index] = {
      ...original,
      ...updates,
      createdAt: original.createdAt,
    };
  }

  restoreDeletedMessage(chatId: string, messageId: string, snapshot: Message) {
    const messages = this.messagesByChatId[chatId];

    if (!messages) {
      return;
    }

    const index = messages.findIndex(function (m) {
      return m.id === messageId;
    });

    if (index === -1) {
      return;
    }

    messages[index] = snapshot;
    const chat = this.chatsById[chatId];

    if (chat) {
      const latest = this.findLatestNonDeletedMessage(chatId);

      if (latest && latest.id === messageId) {
        chat.lastMessage = latest;
      }
    }
  }

  applyMessageEdit(params: {
    chatId: string;
    messageId: string;
    content: unknown;
    editedAt: string;
  }) {
    const messages = this.messagesByChatId[params.chatId];

    if (!messages) {
      return;
    }

    const index = messages.findIndex(function (m) {
      return m.id === params.messageId;
    });

    if (index === -1) {
      return;
    }

    messages[index] = {
      ...messages[index],
      content: params.content,
      editedAt: params.editedAt,
    };

    const chat = this.chatsById[params.chatId];

    if (chat?.lastMessage?.id === params.messageId) {
      chat.lastMessage = {
        ...chat.lastMessage,
        content: params.content,
        editedAt: params.editedAt,
      };
    }
  }

  removeMessage(chatId: string, messageId: string) {
    const messages = this.messagesByChatId[chatId];

    if (!messages) {
      return;
    }

    this.messagesByChatId[chatId] = messages.filter(function (m) {
      return m.id !== messageId;
    });
  }

  markMessageDeleted(chatId: string, messageId: string) {
    this.updateMessage(chatId, messageId, {
      isDeleted: true,
      content: null,
      attachments: [],
    });
    const chat = this.chatsById[chatId];

    if (chat?.lastMessage?.id === messageId) {
      chat.lastMessage = this.findLatestNonDeletedMessage(chatId);
    }
  }

  private findLatestNonDeletedMessage(chatId: string): Message | null {
    const messages = this.messagesByChatId[chatId];

    if (!messages) {
      return null;
    }

    for (let i = messages.length - 1; i >= 0; i--) {
      if (!messages[i].isDeleted) {
        return messages[i];
      }
    }

    return null;
  }

  addIncomingMessage(
    message: Message,
    options?: { skipUnreadIncrement?: boolean },
  ) {
    const chat = this.chatsById[message.chatId];

    if (!chat) {
      return;
    }

    const existing = this.getMessageById(message.id);

    if (existing) {
      this.updateMessage(message.chatId, message.id, message);
      return;
    }

    this.mergeMessages(message.chatId, [message]);

    if (!chat.lastMessage || message.createdAt > chat.lastMessage.createdAt) {
      chat.lastMessage = message;
    }

    if (
      !message.wasSentBySameUser &&
      !options?.skipUnreadIncrement &&
      !this.isReadAt(message.chatId, message.createdAt)
    ) {
      chat.unreadCount = chat.unreadCount + 1;
    }

    const index = this.chats.findIndex((c) => {
      return c.id === message.chatId;
    });

    if (index > 0) {
      this.chats.splice(index, 1);
      this.chats.unshift(chat);
    }
  }

  createNewChat() {
    this.newChat = { members: [] };
  }

  addNewChatWithMessage(chat: ChatData) {
    const existingChat = this.chatsById[chat.id];

    if (existingChat) {
      return;
    }

    this.chats.unshift(chat);

    if (chat.lastMessage) {
      this.mergeMessages(chat.id, [chat.lastMessage]);
    }
  }

  markChatAsRead(chatId: string) {
    const chat = this.chatsById[chatId];

    if (!chat) {
      return;
    }

    chat.unreadCount = 0;
  }

  applyChatRead(chatId: string, readAt: string) {
    if (this.isReadAt(chatId, readAt)) {
      return;
    }

    this.lastReadAtByChatId[chatId] = readAt;
    const chat = this.chatsById[chatId];

    if (!chat) {
      return;
    }

    const messages = this.messagesByChatId[chatId] ?? [];
    chat.unreadCount = messages.filter(function (m) {
      return !m.wasSentBySameUser && !m.isDeleted && m.createdAt > readAt;
    }).length;
  }

  applyChatUnread(chatId: string, readAt: string) {
    this.lastReadAtByChatId[chatId] = readAt;
    const chat = this.chatsById[chatId];

    if (!chat) {
      return;
    }

    chat.unreadCount = Math.max(chat.unreadCount, 1);
  }

  private isReadAt(chatId: string, createdAt: string) {
    const lastReadAt = this.lastReadAtByChatId[chatId];
    return lastReadAt !== undefined && createdAt <= lastReadAt;
  }

  addMemberToNewChat(member: Member) {
    if (!this.newChat) {
      return;
    }

    const exists = this.newChat.members.some((m) => {
      return m.id === member.id;
    });

    if (exists) {
      return;
    }

    this.newChat.members.push(member);
  }

  removeMemberFromNewChat(memberId: string) {
    if (!this.newChat) {
      return;
    }

    this.newChat.members = this.newChat.members.filter((m) => {
      return m.id !== memberId;
    });
  }

  clearNewChat() {
    this.newChat = null;
  }

  cleanup() {
    this.chats = [];
    this.messagesByChatId = {};
    this.lastReadAtByChatId = {};
    this.newChat = null;
  }

  private mergeMessages(chatId: string, incoming: Message[]) {
    const existing = this.messagesByChatId[chatId] ?? [];
    const byId = new Map<string, Message>();

    for (const message of existing) {
      byId.set(message.id, message);
    }

    for (const message of incoming) {
      byId.set(message.id, message);
    }

    const merged = Array.from(byId.values());
    merged.sort((left, right) => {
      const byTime = left.createdAt.localeCompare(right.createdAt);
      return byTime !== 0 ? byTime : left.id.localeCompare(right.id);
    });
    this.messagesByChatId[chatId] = merged;
  }
}
